import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Badge, Breadcrumb, Card, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { openSlotSidebar } from './openSlotSidebar';
import { apiGet } from '../../lib/api';

interface BookingDto {
  id: string;
  eventTitle: string;
  inviteeName: string;
  inviteeEmail: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'CONFIRMED' | 'CANCELLED';
  meetingUrl: string | null;
}

export function BookingsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [list, setList] = useState<BookingDto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiGet<BookingDto[]>('/api/openslot/bookings').then(setList).catch(() => setErr('Failed to load bookings.'));
  }, []);

  const fmt = (iso: string, tz: string) =>
    new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: tz });

  return (
    <AppChrome secondarySidebar={openSlotSidebar(navigate, pathname)}>
      <PageHeader
        title={<>Open Slot - <span className="fw-normal">Bookings</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Open Slot' }, { label: 'Bookings' }]} />}
      />
      <div className="content">
        {err && <Alert variant="danger" icon="ph-warning">{err}</Alert>}
        {!list && <Spinner />}
        {list && list.length === 0 && (
          <Card title="No bookings yet">
            <p className="mb-0 text-muted">Confirmed bookings from your public links will appear here.</p>
          </Card>
        )}
        {list && list.length > 0 && (
          <div className="card">
            <div className="table-responsive">
              <table className="table text-nowrap mb-0">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Invitee</th>
                    <th>Status</th>
                    <th>Meeting</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((b) => (
                    <tr key={b.id}>
                      <td>{fmt(b.startsAt, b.timezone)}</td>
                      <td className="fw-semibold">{b.eventTitle}</td>
                      <td>
                        {b.inviteeName}
                        <div className="text-muted fs-sm">{b.inviteeEmail}</div>
                      </td>
                      <td>
                        <Badge variant={b.status === 'CONFIRMED' ? 'success' : 'secondary'} soft>
                          {b.status}
                        </Badge>
                      </td>
                      <td>
                        {b.meetingUrl ? (
                          <a href={b.meetingUrl} target="_blank" rel="noreferrer">Join</a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppChrome>
  );
}
